import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { I18nProvider } from "@/lib/i18n";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CountryProvider } from "@/lib/countryContext";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useTrackBehavior } from "@/hooks/useTrackBehavior";
// KseniaChat and GlobalSosButton moved to SiteNavbar for context-aware access
import GDPRBanner from "@/components/GDPRBanner";
// force HMR refresh
import OfflineIndicator from "./components/OfflineIndicator";
import OnboardingGuide from "./components/OnboardingGuide";
import WelcomeDialog from "./components/WelcomeDialog";
import UpdatePopup from "./components/UpdatePopup";
import UpdateBanner from "./components/UpdateBanner";
import AppUpdatePrompt from "./components/AppUpdatePrompt";
import BottomNav from "./components/BottomNav";
import MobileViewportManager from "./components/MobileViewportManager";
import SmartNotificationToast from "./components/SmartNotificationToast";
import MinimizedNavBanner from "./components/navigation/MinimizedNavBanner";
import GuestReEngageToast from "./components/auth/GuestReEngageToast";

// Eagerly loaded (critical path)
const Home = lazy(() => import("./pages/Home"));
import NotFound from "./pages/NotFound";

// Lazy-loaded pages for code splitting
const SearchPage = lazy(() => import("./pages/SearchPage"));
const CampingDetail = lazy(() => import("./pages/CampingDetail"));
const FavoritesPage = lazy(() => import("./pages/FavoritesPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const TripsPage = lazy(() => import("./pages/TripsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SubmitCamping = lazy(() => import("./pages/SubmitCamping"));
const MySubmissionsPage = lazy(() => import("./pages/MySubmissionsPage"));
const RoadTipsPage = lazy(() => import("./pages/RoadTipsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SharedTripPage = lazy(() => import("./pages/SharedTripPage"));
const CampingMapPage = lazy(() => import("./pages/CampingMapPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const IntegrationTermsPage = lazy(() => import("./pages/IntegrationTermsPage"));
// CountryGuidesPage merged into TravelGuidesPage — redirect handled in routes
const ServiceEmergencyPage = lazy(() => import("./pages/ServiceEmergencyPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SosPage = lazy(() => import("./pages/SosPage"));
const PublicTripPage = lazy(() => import("./pages/PublicTripPage"));
// ComparePage removed — comparison accessible via search/favorites

const MyTripsPage = lazy(() => import("./pages/MyTripsPage"));
const TravelGuidesPage = lazy(() => import("./pages/TravelGuidesPage"));
const CountryTravelGuidesPage = lazy(() => import("./pages/CountryTravelGuidesPage"));
const TravelGuideDetailPage = lazy(() => import("./pages/TravelGuideDetailPage"));
const ChecklistsPage = lazy(() => import("./pages/ChecklistsPage"));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const WhatsNewPage = lazy(() => import("./pages/WhatsNewPage"));
const GamesPage = lazy(() => import("./pages/GamesPage"));
const BudgetPage = lazy(() => import("./pages/BudgetPage"));
const DestinationPage = lazy(() => import("./pages/DestinationPage"));
const RegionPage = lazy(() => import("./pages/RegionPage"));
const InstagramLandingPage = lazy(() => import("./pages/InstagramLandingPage"));
const TikTokLandingPage = lazy(() => import("./pages/TikTokLandingPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const VehiclePage = lazy(() => import("./pages/VehiclePage"));
const VehicleTripPage = lazy(() => import("./pages/VehicleTripPage"));
const NavigatePage = lazy(() => import("./pages/NavigatePage"));
const MyMapPage = lazy(() => import("./pages/MyMapPage"));
const CuratedGuidesPage = lazy(() => import("./pages/CuratedGuidesPage"));
const CuratedGuideDetailPage = lazy(() => import("./pages/CuratedGuideDetailPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 min — fresher data
      gcTime: 15 * 60 * 1000,   // 15 min cache
      refetchOnWindowFocus: true, // Auto-refresh when user returns to app
      refetchOnReconnect: true,   // Refresh after network reconnect
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen bg-background">
    {/* Skeleton navbar */}
    <div className="h-16 border-b border-border/50 bg-card/50 backdrop-blur" />
    <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
      {/* Skeleton title */}
      <div className="space-y-2">
        <div className="h-8 w-48 rounded-lg bg-muted animate-pulse" />
        <div className="h-4 w-72 rounded bg-muted/70 animate-pulse" />
      </div>
      {/* Skeleton cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="space-y-3">
            <div className="aspect-[4/3] rounded-xl bg-muted animate-pulse" />
            <div className="h-4 w-3/4 rounded bg-muted/70 animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-muted/50 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const AppRoutes = () => {
  usePageTracking();
  useAutoRefresh();
  useTrackBehavior();

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Core pages */}
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/map" element={<CampingMapPage />} />
          <Route path="/camping/:slug" element={<CampingDetail />} />
          <Route path="/compare" element={<Navigate to="/search" replace />} />

          {/* Trip planner */}
          <Route path="/trips" element={<TripsPage />} />
          <Route path="/my-trips" element={<MyTripsPage />} />
          <Route path="/checklists" element={<ChecklistsPage />} />
          <Route path="/trip/share/:token" element={<SharedTripPage />} />
          <Route path="/trip/:tripId" element={<PublicTripPage />} />
          <Route path="/navigate/:tripId" element={<NavigatePage />} />

          {/* User pages */}
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/my-map" element={<MyMapPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/achievements" element={<AchievementsPage />} />
          <Route path="/budget" element={<BudgetPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="/vehicle" element={<VehiclePage />} />
          <Route path="/vehicle-trip" element={<VehicleTripPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/submit" element={<SubmitCamping />} />
          <Route path="/my-submissions" element={<MySubmissionsPage />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Content */}
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/road-tips" element={<RoadTipsPage />} />
          <Route path="/guides" element={<Navigate to="/travel-guides" replace />} />
          <Route path="/travel-guides" element={<TravelGuidesPage />} />
          <Route path="/travel-guides/:countryCode" element={<CountryTravelGuidesPage />} />
          <Route path="/travel-guides/:countryCode/:guideSlug" element={<TravelGuideDetailPage />} />
          <Route path="/curated-guides" element={<CuratedGuidesPage />} />
          <Route path="/curated-guides/:slug" element={<CuratedGuideDetailPage />} />
          <Route path="/services" element={<ServiceEmergencyPage />} />
          <Route path="/sos/:token" element={<SosPage />} />

          {/* Admin, games & legal */}
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/games" element={<GamesPage />} />
          <Route path="/destination/:countryCode" element={<DestinationPage />} />
          <Route path="/destination/:countryCode/:regionSlug" element={<RegionPage />} />
          <Route path="/instagram" element={<InstagramLandingPage />} />
          <Route path="/tiktok" element={<TikTokLandingPage />} />
          <Route path="/whats-new" element={<WhatsNewPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/integration-terms" element={<IntegrationTermsPage />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <BottomNav />
      {/* StickySignupButton removed */}
      <GDPRBanner />
      <OnboardingGuide />
      <WelcomeDialog />
      <UpdatePopup />
      <UpdateBanner />
      <SmartNotificationToast />
      <MinimizedNavBanner />
      <GuestReEngageToast />
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <I18nProvider>
          <CountryProvider>
            <AuthProvider>
              <MobileViewportManager />
              <Toaster />
              <OfflineIndicator />
              <AppUpdatePrompt />
              <BrowserRouter>
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              </BrowserRouter>
            </AuthProvider>
          </CountryProvider>
        </I18nProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
